import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import authService from '../services/authService';
import '../styles/profile.css';

const Profile = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [changingPassword, setChangingPassword] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [passwordMessage, setPasswordMessage] = useState('');
    const [user, setUser] = useState(null);
    const [profileForm, setProfileForm] = useState({
        name: '',
        username: '',
        email: '',
        phone: '',
        role: 'customer'
    });
    const [passwordForm, setPasswordForm] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });

    const syncProfileForm = useCallback((profileUser) => {
        if (!profileUser) return;
        setProfileForm({
            name: profileUser.name || profileUser.username || '',
            username: profileUser.username || profileUser.name || '',
            email: profileUser.email || '',
            phone: profileUser.phone || '',
            role: profileUser.role || 'customer'
        });
    }, []);

    const fetchProfile = useCallback(async () => {
        setLoading(true);
        setError('');

        const localUser = authService.getCurrentUser();
        if (!authService.isLoggedIn()) {
            setUser(null);
            setLoading(false);
            return;
        }

        try {
            const result = await authService.getProfile();
            if (result.success) {
                const profileUser = result.user?.data || result.user?.user || result.user;
                const normalizedUser = profileUser || localUser;
                setUser(normalizedUser);
                syncProfileForm(normalizedUser);
                localStorage.setItem('user', JSON.stringify(normalizedUser));
            } else if (localUser) {
                setUser(localUser);
                syncProfileForm(localUser);
            } else {
                setError(result.message || 'Unable to load your profile right now.');
            }
        } catch (fetchError) {
            if (localUser) {
                setUser(localUser);
                syncProfileForm(localUser);
            } else {
                setError('Unable to load your profile right now.');
            }
        } finally {
            setLoading(false);
        }
    }, [syncProfileForm]);

    useEffect(() => {
        fetchProfile();
    }, [fetchProfile]);

    const profileStats = useMemo(() => {
        const displayName = profileForm.name || profileForm.username || 'Traveler';
        return [
            {
                label: 'Account Status',
                value: authService.isLoggedIn() ? 'Active' : 'Guest',
                icon: 'fas fa-badge-check'
            },
            {
                label: 'Member Type',
                value: (profileForm.role || 'customer').toUpperCase(),
                icon: 'fas fa-user-shield'
            },
            {
                label: 'Primary Contact',
                value: profileForm.phone || 'Not added',
                icon: 'fas fa-phone'
            },
            {
                label: 'Display Name',
                value: displayName,
                icon: 'fas fa-id-card'
            }
        ];
    }, [profileForm]);

    const handleProfileChange = (e) => {
        const { name, value } = e.target;
        setProfileForm((prev) => ({
            ...prev,
            [name]: value
        }));
        setSuccessMessage('');
        setError('');
    };

    const handlePasswordChange = (e) => {
        const { name, value } = e.target;
        setPasswordForm((prev) => ({
            ...prev,
            [name]: value
        }));
        setPasswordMessage('');
        setError('');
    };

    const handleProfileSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError('');
        setSuccessMessage('');

        try {
            const payload = {
                name: profileForm.name,
                username: profileForm.username || profileForm.name,
                email: profileForm.email,
                phone: profileForm.phone
            };

            const result = await authService.updateProfile(payload);
            if (result.success) {
                const updatedUser = result.data || result.user || payload;
                const normalizedUser = {
                    ...user,
                    ...updatedUser,
                    name: updatedUser.name || payload.name,
                    username: updatedUser.username || payload.username
                };
                setUser(normalizedUser);
                syncProfileForm(normalizedUser);
                localStorage.setItem('user', JSON.stringify(normalizedUser));
                window.dispatchEvent(new Event('authChange'));
                setSuccessMessage(result.message || 'Profile updated successfully.');
            } else {
                throw new Error(result.message || 'Failed to update profile.');
            }
        } catch (submitError) {
            const fallbackUser = {
                ...user,
                name: profileForm.name,
                username: profileForm.username || profileForm.name,
                email: profileForm.email,
                phone: profileForm.phone
            };
            setUser(fallbackUser);
            localStorage.setItem('user', JSON.stringify(fallbackUser));
            window.dispatchEvent(new Event('authChange'));
            setSuccessMessage('Profile saved locally. Live profile sync may not be available yet.');
        } finally {
            setSaving(false);
        }
    };

    const handlePasswordSubmit = async (e) => {
        e.preventDefault();
        setChangingPassword(true);
        setPasswordMessage('');
        setError('');

        if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
            setError('Please fill all password fields.');
            setChangingPassword(false);
            return;
        }

        if (passwordForm.newPassword.length < 6) {
            setError('New password must be at least 6 characters.');
            setChangingPassword(false);
            return;
        }

        if (passwordForm.newPassword !== passwordForm.confirmPassword) {
            setError('New password and confirm password do not match.');
            setChangingPassword(false);
            return;
        }

        try {
            const result = await authService.changePassword({
                currentPassword: passwordForm.currentPassword,
                newPassword: passwordForm.newPassword
            });

            if (!result.success) {
                throw new Error(result.message || 'Failed to change password.');
            }

            setPasswordForm({
                currentPassword: '',
                newPassword: '',
                confirmPassword: ''
            });
            setPasswordMessage(result.message || 'Password changed successfully.');
        } catch (submitError) {
            setError(submitError.message || 'Failed to change password.');
        } finally {
            setChangingPassword(false);
        }
    };

    if (!authService.isLoggedIn()) {
        return (
            <div className="profile-page">
                <section className="profile-hero">
                    <div className="profile-hero-content">
                        <span className="profile-badge">Traveler Account</span>
                        <h1>My Profile</h1>
                        <p>Login to manage your personal details, contact information and password.</p>
                    </div>
                </section>
                <div className="profile-container">
                    <div className="profile-empty-card">
                        <div className="profile-empty-icon">
                            <i className="fas fa-user-lock"></i>
                        </div>
                        <h2>Login Required</h2>
                        <p>Please sign in first to view and manage your profile.</p>
                        <Link to="/login" className="profile-primary-btn">Login Now</Link>
                    </div>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="profile-page">
                <div className="profile-loading">
                    <div className="profile-spinner"></div>
                    <p>Loading your profile...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="profile-page">
            <section className="profile-hero">
                <div className="profile-hero-content">
                    <span className="profile-badge">Traveler Account</span>
                    <h1>My Profile</h1>
                    <p>Manage your personal details, saved contact information and account security from one clean dashboard.</p>
                </div>
            </section>

            <div className="profile-container">
                <div className="profile-summary-card">
                    <div className="profile-summary-head">
                        <div className="profile-avatar">
                            {(profileForm.name || profileForm.username || 'U').charAt(0).toUpperCase()}
                        </div>
                        <div className="profile-summary-copy">
                            <span className="summary-eyebrow">Account Overview</span>
                            <h2>{profileForm.name || profileForm.username || 'Traveler'}</h2>
                            <p>{profileForm.email || 'No email added yet'}</p>
                        </div>
                    </div>

                    <div className="profile-stats-grid">
                        {profileStats.map((item) => (
                            <div key={item.label} className="profile-stat-card">
                                <div className="profile-stat-icon">
                                    <i className={item.icon}></i>
                                </div>
                                <div>
                                    <span>{item.label}</span>
                                    <strong>{item.value}</strong>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {(error || successMessage || passwordMessage) && (
                    <div className="profile-alert-stack">
                        {error && (
                            <div className="profile-alert error">
                                <i className="fas fa-circle-exclamation"></i>
                                <span>{error}</span>
                            </div>
                        )}
                        {successMessage && (
                            <div className="profile-alert success">
                                <i className="fas fa-circle-check"></i>
                                <span>{successMessage}</span>
                            </div>
                        )}
                        {passwordMessage && (
                            <div className="profile-alert success">
                                <i className="fas fa-shield-check"></i>
                                <span>{passwordMessage}</span>
                            </div>
                        )}
                    </div>
                )}

                <div className="profile-grid">
                    <section className="profile-panel">
                        <div className="panel-head">
                            <h3>Personal Information</h3>
                            <p>Keep your main account details up to date for smoother booking and support.</p>
                        </div>

                        <form className="profile-form" onSubmit={handleProfileSubmit}>
                            <div className="profile-form-grid">
                                <label className="profile-field">
                                    <span>Full Name</span>
                                    <input
                                        type="text"
                                        name="name"
                                        value={profileForm.name}
                                        onChange={handleProfileChange}
                                        placeholder="Enter your full name"
                                    />
                                </label>

                                <label className="profile-field">
                                    <span>Username</span>
                                    <input
                                        type="text"
                                        name="username"
                                        value={profileForm.username}
                                        onChange={handleProfileChange}
                                        placeholder="Choose a username"
                                    />
                                </label>

                                <label className="profile-field">
                                    <span>Email Address</span>
                                    <input
                                        type="email"
                                        name="email"
                                        value={profileForm.email}
                                        onChange={handleProfileChange}
                                        placeholder="your@email.com"
                                    />
                                </label>

                                <label className="profile-field">
                                    <span>Phone Number</span>
                                    <input
                                        type="tel"
                                        name="phone"
                                        value={profileForm.phone}
                                        onChange={handleProfileChange}
                                        placeholder="Enter phone number"
                                    />
                                </label>
                            </div>

                            <div className="profile-actions">
                                <button type="submit" className="profile-primary-btn" disabled={saving}>
                                    {saving ? 'Saving...' : 'Save Changes'}
                                </button>
                            </div>
                        </form>
                    </section>

                    <section className="profile-panel">
                        <div className="panel-head">
                            <h3>Security</h3>
                            <p>Update your password regularly to keep your account secure.</p>
                        </div>

                        <form className="profile-form" onSubmit={handlePasswordSubmit}>
                            <div className="profile-form-grid single">
                                <label className="profile-field">
                                    <span>Current Password</span>
                                    <input
                                        type="password"
                                        name="currentPassword"
                                        value={passwordForm.currentPassword}
                                        onChange={handlePasswordChange}
                                        placeholder="Enter current password"
                                    />
                                </label>

                                <label className="profile-field">
                                    <span>New Password</span>
                                    <input
                                        type="password"
                                        name="newPassword"
                                        value={passwordForm.newPassword}
                                        onChange={handlePasswordChange}
                                        placeholder="Enter new password"
                                    />
                                </label>

                                <label className="profile-field">
                                    <span>Confirm New Password</span>
                                    <input
                                        type="password"
                                        name="confirmPassword"
                                        value={passwordForm.confirmPassword}
                                        onChange={handlePasswordChange}
                                        placeholder="Confirm new password"
                                    />
                                </label>
                            </div>

                            <div className="profile-note">
                                <i className="fas fa-shield-heart"></i>
                                <p>Use at least 6 characters and avoid sharing your password with anyone.</p>
                            </div>

                            <div className="profile-actions">
                                <button type="submit" className="profile-secondary-btn" disabled={changingPassword}>
                                    {changingPassword ? 'Updating...' : 'Change Password'}
                                </button>
                            </div>
                        </form>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default Profile;
